import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import {
  CollapsibleCard,
  Error,
  Radio,
  ResponsiveFlex,
  Row,
  TextInput,
  Tooltip,
} from "@narmi/design_system";

import * as formAdapters from "../../../../adapters";
import SectionActions from "../../common/SectionActions";
import SectionError from "../../common/SectionError";
import SectionHeader from "../../common/SectionHeader";

import type { RecipientProfileType } from "byzantine/src/dbbl/businessLogic/entities/recipients";

interface ProfileSectionProps {
  mode: "create" | "edit";
  recipientId?: API.RecipientId;
  initialValues?: Partial<RecipientProfileType>;
  isOpen: boolean;
  isComplete: boolean;
  // input the user hasn't saved is blocking another section
  showUnsavedError: boolean;
  needsReview: boolean;
  onToggle: () => void;
  onSaved: (recipientId: API.RecipientId) => void;
  showCancel: boolean;
  onCancel: () => void;
}

// saves the recipient's profile with its own API call: creates the recipient in create mode
const ProfileSection = ({
  mode,
  recipientId,
  initialValues,
  isOpen,
  isComplete,
  showUnsavedError,
  needsReview,
  onToggle,
  onSaved,
  showCancel,
  onCancel,
}: ProfileSectionProps) => {
  const { l10n } = useLocalization();
  const { form, handleSubmit, errorMessage, loading } =
    entities.recipients.useRecipientProfileForm(recipientId, initialValues);
  const purposeOptions = entities.recipients.useProfilePurposeOptions();
  const isCreate = mode === "create";
  const { error: typeError } = formAdapters.radio(form, "type");

  const handleClickSave = () =>
    handleSubmit((recipient) => {
      form.resetForm({ values: form.values });
      onSaved(recipient.id);
    });

  const handleClickCancel = () => {
    form.resetForm();
    onCancel();
  };

  return (
    <CollapsibleCard
      isOpen={isOpen}
      onOpen={onToggle}
      onClose={onToggle}
      trigger="header"
      renderTitle={() => (
        <SectionHeader
          title={l10n.getString("recipient-drawer-profile-title")}
          isOpen={isOpen}
          stepNumber={isCreate ? 1 : undefined}
          isComplete={isComplete}
          badgeLabel={
            needsReview && !recipientId
              ? l10n.getString("recipient-drawer-section-needs-review")
              : undefined
          }
        />
      )}
    >
      <ResponsiveFlex gapSize="s">
        <SectionError
          errorMessage={errorMessage}
          showUnsavedError={showUnsavedError}
        />
        <Row gapSize="m">
          {Object.entries(purposeOptions).map(([label, value]) => {
            // the error shows once, under both cards
            const { error, ...radioProps } = formAdapters.radioSingle(
              form,
              "type",
              value
            );
            return (
              <Row.Item key={value}>
                <Radio
                  kind="input-card"
                  hasError={Boolean(error)}
                  {...radioProps}
                >
                  <span className="fontWeight--normal">{label}</span>
                </Radio>
              </Row.Item>
            );
          })}
        </Row>
        <Error error={typeError} marginTop="none" />
        <TextInput
          label={l10n.getString("recipient-drawer-profile-name-label")}
          {...formAdapters.input(form, "name")}
        />
        <TextInput
          label={l10n.getString("recipient-drawer-profile-nickname-label")}
          endContent={
            <Tooltip
              text={l10n.getString("add-recipient-drawer-nickname-tooltip")}
            >
              <i className="narmi-icon-info" />
            </Tooltip>
          }
          {...formAdapters.input(form, "nickname")}
        />
        <SectionActions
          testIdSuffix="recipient-profile"
          showCancel={showCancel}
          isSaving={loading}
          onCancel={handleClickCancel}
          onSave={handleClickSave}
        />
      </ResponsiveFlex>
    </CollapsibleCard>
  );
};

export default ProfileSection;
